import * as React from "react";
import { ChevronDown } from "lucide-react";
import { SearchablePopover, useToast, cn } from "@racoon/ui";
import { Namespace, useT, useLang } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { isShiftTerminal } from "~/shared/lib/shift-status";
import { fromTimestamp, normalizeForSearch } from "@racoon/lib";
import { useListDestinationLocationsQuery } from "~/shared/api/locations/locations.grpc";
import { useUpdateRouteWithJobsMutation } from "~/shared/api/routes/route.grpc";
import {
  getRouteFactVolume,
  getRouteJobsCount,
  getRouteTotalVolume,
} from "../../utils/routeMetrics";
import { unloadLocationsArgs } from "./unload-locations";

type Props = {
  route: ConnectProto.Route;
  orgId: bigint;
  onRefetch?: () => void;
  /** When set (TTN page), picking a place hands it over instead of saving the route. */
  onDestinationChange?: (route: ConnectProto.Route, destination: ConnectProto.Location) => void;
  disabled?: boolean;
  /** TTN: the jobs' fact volume ahead of the nominal one. */
  showFactVolume?: boolean;
};

/**
 * A shift that has ended can no longer be re-routed, so its unload point becomes
 * read-only; while the shift is running the dispatcher may still change it.
 */
/** BCP 47 tags for `toLocaleTimeString`; the app's own codes aren't valid tags. */
const TIME_LOCALE_MAP: Record<string, string> = {
  en: "en-GB",
  ru: "ru-RU",
  kk: "kk-KZ",
};

const isShiftActive = (shift?: ConnectProto.DriverOnShift) =>
  shift != null && !isShiftTerminal(shift.status) && shift.completedAt == null;

/**
 * Row shown under the route's job table: the unload point (destination location)
 * picker on the left, route totals on the right.
 */
export const RouteUnloadRow: React.FC<Props> = ({
  route,
  orgId,
  onRefetch,
  onDestinationChange,
  disabled = false,
  showFactVolume = false,
}) => {
  const t = useT();
  const { lang } = useLang();
  const { toast } = useToast();
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [updateRouteWithJobs, { isLoading: isSaving }] = useUpdateRouteWithJobsMutation();

  // Destination locations include disposal sites of the organizations linked in
  // settings — a route often unloads at a polygon another organization owns.
  // currentData: another organization's locations must not stay pickable while
  // this one's load.
  const { currentData: locationsResponse, isFetching: isFetchingLocations } =
    useListDestinationLocationsQuery(unloadLocationsArgs(orgId));
  const isLocationsLoading = isFetchingLocations && !locationsResponse;

  const locations = React.useMemo(() => locationsResponse?.locations ?? [], [locationsResponse]);

  // Only active locations can be picked, but the route's current destination is
  // resolved against the unfiltered list: a point that was later deactivated is
  // still what this route unloads at, and must render as such.
  const activeLocations = React.useMemo(
    () => locations.filter((location) => location.status === ConnectProto.LocationStatus.ACTIVE),
    [locations],
  );

  const filteredLocations = React.useMemo(() => {
    const query = normalizeForSearch(searchQuery);
    if (!query) return activeLocations;
    return activeLocations.filter((location) => normalizeForSearch(location.name).includes(query));
  }, [activeLocations, searchQuery]);

  const selectedLocation = React.useMemo(
    () =>
      route.destinationLocationId != null
        ? locations.find((location) => location.id === route.destinationLocationId)
        : undefined,
    [locations, route.destinationLocationId],
  );

  // The TTN is filled in after the shift, so there the place stays editable.
  const isEditable = Boolean(onDestinationChange) || isShiftActive(route.driverOnShift);

  const valueLabel = isLocationsLoading
    ? t(Namespace.Common, "loading")
    : (selectedLocation?.name ?? t(Namespace.Depot, "selectDepot"));

  const handleSelectLocation = async (location: ConnectProto.Location) => {
    setIsPopoverOpen(false);
    setSearchQuery("");
    if (location.id === route.destinationLocationId) return;
    if (onDestinationChange) return onDestinationChange(route, location);

    try {
      await updateRouteWithJobs({
        route: {
          id: route.id,
          destinationLocationId: location.id,
          destinationLon: location.lon,
          destinationLat: location.lat,
        },
        // The endpoint replaces the route's job set, so the current jobs are
        // resent unchanged — only the destination is being updated here.
        jobIds: route.jobs.map((job) => job.id),
        ownerOrganizationId: orgId,
      }).unwrap();

      toast({
        title: t(Namespace.Routes, "success"),
        description: t(Namespace.Depot, "updated"),
      });
      onRefetch?.();
    } catch (error) {
      toast({
        title: t(Namespace.Routes, "error"),
        description: t(Namespace.Depot, "updateError"),
        variant: "destructive",
      });
      console.error("Error updating route unload point:", error);
    }
  };

  const jobsCount = getRouteJobsCount(route);
  const totalVolume = getRouteTotalVolume(route);
  // Nothing planned reads as "—"; a fact of zero (every container empty) is a
  // real figure and is shown as such.
  const volumeLabel = (volume: number, keepZero = false) =>
    t(Namespace.Routes, "volumeCubicMeters", {
      volume: volume > 0 || keepZero ? Number(volume.toFixed(3)) : "—",
    });

  const durationText = React.useMemo(() => {
    const start = fromTimestamp(route.factStartAt) ?? fromTimestamp(route.planStartAt);
    const end = fromTimestamp(route.factEndAt) ?? fromTimestamp(route.planEndAt);
    if (!start || !end) return "—";

    const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourUnit = t(Namespace.Routes, "hourShortUnit");
    const minuteUnit = t(Namespace.Routes, "minuteShortUnit");
    const duration =
      hours > 0 ? `${hours}${hourUnit} ${minutes}${minuteUnit}` : `${minutes}${minuteUnit}`;

    const locale = TIME_LOCALE_MAP[lang] ?? "ru-RU";
    const endTime = end.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${duration} (${endTime})`;
  }, [route.factStartAt, route.planStartAt, route.factEndAt, route.planEndAt, lang, t]);

  const value = (
    <>
      <span className="truncate text-sm text-text-primary">{valueLabel}</span>
      <ChevronDown className="h-4 w-4 shrink-0 text-icon-secondary" />
    </>
  );

  return (
    <div className="flex items-center justify-between gap-4 px-2 py-2">
      <div className="flex h-8 w-[440px] max-w-full items-center overflow-hidden rounded-lg border border-border-primary bg-white">
        <div className="flex h-full w-[100px] shrink-0 items-center justify-center border-r border-border-primary bg-border-primary px-4">
          <span className="text-sm font-medium text-text-primary">
            {t(Namespace.Depot, "label")}
          </span>
        </div>

        {isEditable ? (
          <SearchablePopover
            open={isPopoverOpen}
            onOpenChange={(open) => {
              setIsPopoverOpen(open);
              if (!open) setSearchQuery("");
            }}
            trigger={
              <button
                type="button"
                disabled={isSaving || disabled}
                className="flex h-full min-w-0 flex-1 items-center justify-end gap-1 px-2 hover:bg-gray-50 focus:outline-none disabled:opacity-60"
              >
                {value}
              </button>
            }
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            inputId={`route-${route.id}-unload-search-input`}
            placeholder={t(Namespace.Depot, "search")}
          >
            {filteredLocations.map((location) => (
              <button
                key={String(location.id)}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectLocation(location);
                }}
                className={cn(
                  "w-full px-4 py-2 text-left text-sm hover:bg-gray-100",
                  location.id === route.destinationLocationId && "bg-blue-50 text-blue-600",
                )}
              >
                {location.name}
              </button>
            ))}
            {filteredLocations.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                {searchQuery.trim()
                  ? t(Namespace.Depot, "notFound")
                  : t(Namespace.Depot, "noneAvailable")}
              </div>
            )}
          </SearchablePopover>
        ) : (
          <div
            aria-disabled
            className="flex h-full min-w-0 flex-1 cursor-not-allowed items-center justify-end gap-1 bg-gray-50 px-2 opacity-60"
          >
            {value}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 whitespace-nowrap text-xs font-medium text-text-primary">
        <span>
          {jobsCount} {t(Namespace.Routes, "jobsCountShort")}
        </span>
        <span>
          {showFactVolume
            ? `${volumeLabel(getRouteFactVolume(route), true)} / ${volumeLabel(totalVolume)}`
            : volumeLabel(totalVolume)}
        </span>
        <span>{durationText}</span>
      </div>
    </div>
  );
};
